/**
 * Drives the baked static player (play.4klabs.ai) in a cross-origin iframe through its
 * embed contract (packages/vplay/src/boot.ts), exposing the slice of
 * VideoCompositionPlayerRef that ReviewModeOverlay needs.
 *
 * IN:  { type: 'videoStateChange', state } — only from the iframe's window at the iframe's origin.
 * OUT: { type: 'embedCommand', command, args } — posted to that origin only.
 */

import type { VideoCompositionPlayerRef } from '../../video-workspace/VideoCompositionPlayer';
import type { VideoPlaybackState } from '../../../types/videoManifest';

export type EmbeddedPlayer = Pick<VideoCompositionPlayerRef, 'getState' | 'play' | 'pause' | 'seek'> & {
  destroy: () => void;
};

type MessageHost = Pick<Window, 'addEventListener' | 'removeEventListener'>;

interface EmbeddedPlayerOptions {
  frame: Pick<HTMLIFrameElement, 'src' | 'contentWindow'>;
  initialState: VideoPlaybackState;
  onState?: (state: VideoPlaybackState) => void;
  host?: MessageHost;
}

export function createEmbeddedPlayer({ frame, initialState, onState, host = window }: EmbeddedPlayerOptions): EmbeddedPlayer {
  const origin = new URL(frame.src).origin;
  let state = initialState;

  const onMessage = (e: MessageEvent) => {
    if (e.origin !== origin || e.source !== frame.contentWindow) return;
    const msg = e.data;
    if (msg?.type !== 'videoStateChange' || !isPlaybackState(msg.state)) return;
    state = { ...state, ...msg.state };
    onState?.(state);
  };
  host.addEventListener('message', onMessage);

  const send = (command: string, args?: Record<string, unknown>) => {
    frame.contentWindow?.postMessage({ type: 'embedCommand', command, args }, origin);
  };

  return {
    getState: () => state,
    play: () => send('play'),
    pause: () => send('pause'),
    // The player's seek command hardcodes pauseAfterSeek:false, so a paused seek is seek + pause.
    seek: (time, options) => {
      send('seek', { time });
      if (options?.pauseAfterSeek) send('pause');
    },
    destroy: () => host.removeEventListener('message', onMessage),
  };
}

function isPlaybackState(value: unknown): value is Partial<VideoPlaybackState> {
  if (!value || typeof value !== 'object') return false;
  const s = value as Record<string, unknown>;
  return typeof s.currentTime === 'number' && typeof s.currentSlideIndex === 'number' && typeof s.isPlaying === 'boolean';
}
